import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Truck, Handshake, Award, CalendarClock, TrendingUp, Phone, ArrowRight, MessageSquare } from 'lucide-react';
const QUICK_ACTIONS = [{
  icon: Phone,
  title: 'No Foreign Call Centers',
  desc: 'Talk with US-based people who understand drivers, HOS, brokers, lanes, and freight.',
  cta: 'How It Works',
  link: '/dispatch-services',
  type: 'route'
}, {
  icon: CalendarClock,
  title: 'Next Load Planned Early',
  desc: 'FSG works ahead so your truck spends less time sitting.',
  cta: 'View Dispatch Support',
  link: '/dispatch-services',
  type: 'route'
}, {
  icon: Truck,
  title: 'Want Dispatch & Load Planning Service',
  desc: 'Have your own MC? Want to start making more money with less stress. Let us manage your Load planning, Dispatching and Billing',
  cta: 'Start Dispatch',
  link: '/dispatch-services',
  type: 'route'
}, {
  icon: Handshake,
  title: 'US-Based Carrier',
  desc: 'Lease on with FSG or ask which path fits your truck.',
  cta: 'Apply to Lease On',
  link: '/lease-on-with-fsg',
  type: 'route'
}];
export function Hero() {
  const renderAction = (action: (typeof QUICK_ACTIONS)[0]) => {
    const content = <div className="bg-black/40 backdrop-blur-md border border-white/20 border-t-[3px] border-t-red p-5 rounded-xl hover:border-white/40 hover:bg-black/50 hover:shadow-2xl transition-all group h-full flex flex-col" data-id="element-269">
        <action.icon className="w-6 h-6 text-red mb-3 group-hover:scale-110 transition-transform" data-id="element-270" />
        <h3 className="text-white font-bold text-sm md:text-base mb-1 leading-snug" data-id="element-271">
          {action.title}
        </h3>
        <p className="text-off-white/80 text-xs md:text-sm font-medium mb-4 flex-grow" data-id="element-272">
          {action.desc}
        </p>
        <span className="inline-flex items-center justify-between w-fit gap-3 mt-auto px-5 py-2.5 bg-red/10 border-2 border-[#ff4d4d] text-[#ff4d4d] font-black text-xs uppercase tracking-wider rounded-md group-hover:bg-red group-hover:text-white group-hover:border-red transition-all" data-id="element-273">
          {action.cta} <ArrowRight className="w-4 h-4" data-id="element-274" />
        </span>
      </div>;
    if (action.type === 'route') {
      return <Link to={action.link} key={action.title} className="block h-full" data-id="element-275">
          {content}
        </Link>;
    }
    if (action.type === 'hash') {
      return <a href={action.link} key={action.title} onClick={e => {
        e.preventDefault();
        document.querySelector(action.link)?.scrollIntoView({
          behavior: 'smooth'
        });
      }} className="block h-full" data-id="element-276">
          {content}
        </a>;
    }
    return <a href={action.link} key={action.title} className="block h-full" data-id="element-277">
        {content}
      </a>;
  };
  return <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden bg-navy" data-id="element-278">
      {/* Full-hero background video */}
      <div className="absolute inset-0 z-0 bg-navy" style={{
      clipPath: 'inset(0)'
    }} data-id="element-279">
        <video autoPlay loop muted playsInline className="fixed md:absolute top-0 left-0 w-full h-[100dvh] md:h-full object-cover object-center opacity-80" data-id="element-280">
          <source src="https://cdn.magicpatterns.com/uploads/fA67FLH4oPtAB4qXFj3nVB/Shane_merem.mp4" type="video/mp4" data-id="element-281" />
        </video>
        {/* Horizontal gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/75 to-navy/40" data-id="element-282" />
        {/* Vertical gradient to seamlessly blend into the dispatch counter below */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy via-navy/80 to-transparent" data-id="element-283" />
      </div>

      <div className="container-custom relative z-10" data-id="element-284">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-285">
          {/* Left: Text & CTAs */}
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-5" data-id="element-286">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-black text-white leading-[1.08] mb-5 tracking-tight font-['Barlow']" data-id="element-287">
              Lease On With FSG, a US-Based Carrier and Dispatch Team
            </h1>

            <p className="text-base md:text-lg text-off-white/85 font-medium mb-5 max-w-xl leading-relaxed" data-id="element-288">
              FSG Transport helps owner-operators lease on under FSG or keep
              running under their own authority with dispatch, load planning,
              broker calls, paperwork, billing, and back-office follow-through.
            </p>

            <p className="inline-flex items-center text-sm md:text-base text-white font-bold mb-8 border-l-2 border-amber pl-3 leading-snug" data-id="element-289">
              No foreign call centers. No outsourced dispatch. US-based people
              who speak clear English and understand trucking.
            </p>

            <div className="flex flex-wrap items-center gap-3" data-id="element-290">
              <button className="hidden lg:flex items-center gap-3 bg-navy text-white px-4 py-3 rounded-full shadow-2xl hover:bg-navy/90 hover:scale-105 transition-all group border border-white/10" aria-label="Open quick answer chat" data-id="element-291">
                <div className="flex flex-col items-start pr-2" data-id="element-292">
                  <span className="text-sm font-bold leading-none" data-id="element-293">
                    Need a Quick Answer?
                  </span>
                  <span className="text-[10px] text-off-white/70 uppercase tracking-wider mt-1" data-id="element-294">
                    Ask FSG
                  </span>
                </div>
                <div className="bg-red w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-inner" data-id="element-295">
                  <MessageSquare className="w-5 h-5 text-white" data-id="element-296" />
                </div>
              </button>
            </div>
          </motion.div>

          {/* Right: Quick Actions Command Center */}
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-7" data-id="element-297">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-298">
              {QUICK_ACTIONS.map(renderAction)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}